import { useEffect, useRef } from "react";
import { getToken, onMessage, isSupported } from "firebase/messaging";
import { messaging } from "../firebase";
import { registerFCMToken } from "../services/notificationService";
import { refreshNotificationCount } from "Pages/Notifications/components/CSCAlerts";

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

async function getActiveSWRegistration(): Promise<ServiceWorkerRegistration> {
  const swReg = await navigator.serviceWorker.register(
    "/firebase-messaging-sw.js",
  );

  // On first load the SW is installing/waiting — wait for THIS specific
  // registration to activate before passing it to getToken.
  if (swReg.active) return swReg;

  return new Promise((resolve) => {
    const sw = swReg.installing ?? swReg.waiting;
    if (!sw) {
      resolve(swReg);
      return;
    }
    sw.addEventListener("statechange", function handler() {
      if (sw.state === "activated") {
        sw.removeEventListener("statechange", handler);
        resolve(swReg);
      }
    });
  });
}

export function useFCM() {
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const initializedRef = useRef(false);

  const initFCM = async (): Promise<boolean> => {
    console.log("initFCM called");

    const supported = await isSupported().catch((e) => {
      return false;
    });
    console.log("supported", supported);

    if (!supported) {
      if (!sessionStorage.getItem("push_unsupported_shown")) {
        sessionStorage.setItem("push_unsupported_shown", "1");
        window.dispatchEvent(new CustomEvent("push-not-supported"));
      }
      return false;
    }
    console.log("messaging instance", messaging);

    if (!messaging || !("serviceWorker" in navigator)) return false;
    try {
      console.log("before SW registration");

      const swReg = await getActiveSWRegistration();
      console.log("SW registered", swReg);

      // @ts-ignore -- getToken is deprecated in Firebase 12 but still required for FCM token generation
      const fcmToken = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swReg,
      });
      console.log("FCM token", fcmToken);

      if (!fcmToken) return false;

      const alreadyRegistered = localStorage.getItem("fcm_token") === fcmToken;
      if (!alreadyRegistered) {
        const result = await registerFCMToken(fcmToken).catch(() => null);
        if (result && !result.error) {
          localStorage.setItem("fcm_token", fcmToken);
        }
      }
      console.log("Before onMessage");
      console.log("messaging", messaging);
      unsubscribeRef.current = onMessage(messaging, (payload) => {
        console.log("payload", payload);
        const title = payload.notification?.title ?? "New Notification";
        const body = payload.notification?.body ?? "";
        const icon = "/favicon.svg";
        swReg.showNotification(title, {
          body,
          icon,
          data: {
            actionUrl: payload.data?.actionUrl || payload.fcmOptions?.link,
          },
        });

        window.dispatchEvent(new CustomEvent("push-notification-received"));
        refreshNotificationCount();
      });
      console.log("After onMessage");
      return true;
    } catch {
      return false;
    }
  };

  const checkAndInit = async () => {
    if (initializedRef.current) return;
    if (!("Notification" in window)) return;
    if (!localStorage.getItem("token")) return;

    switch (Notification.permission) {
      case "granted": {
        const ok = await initFCM();
        if (ok) initializedRef.current = true;
        break;
      }

      case "default": {
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          const ok = await initFCM();
          if (ok) initializedRef.current = true;
        }
        break;
      }

      case "denied":
        break;
    }
  };

  const handleAuthLogout = () => {
    initializedRef.current = false;
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
  };

  useEffect(() => {
    checkAndInit();

    window.addEventListener("auth-ready", checkAndInit);
    window.addEventListener("auth-logout", handleAuthLogout);

    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data?.type !== "NOTIFICATION_CLICK") return;
      const actionUrl = event.data?.actionUrl;
      if (actionUrl) {
        window.location.assign(actionUrl);
      }
    });

    let permStatus: PermissionStatus | null = null;

    if ("permissions" in navigator) {
      navigator.permissions
        .query({ name: "notifications" as PermissionName })
        .then((status) => {
          permStatus = status;
          status.onchange = () => {
            if (status.state === "granted" && localStorage.getItem("token")) {
              initFCM();
            }
          };
        });
    }

    return () => {
      window.removeEventListener("auth-ready", checkAndInit);
      window.removeEventListener("auth-logout", handleAuthLogout);

      if (permStatus) permStatus.onchange = null;

      unsubscribeRef.current?.();
    };
  }, []);
}
